import { RecordLink } from 'components/record-chip/RecordLink';
import { useInRecordSection } from 'components/section-edit/SectionEdit';
import React from 'react';
import { FieldValues } from 'react-hook-form';
import NumberFormat from 'react-number-format';
import { cn } from 'utils/cn';

interface Props<TOption extends FieldValues> {
    value: string | TOption | TOption[];
    getValue?: (option: TOption | null) => string | number | null | undefined;
    getLabel?: (option: TOption | null) => string | number | null | undefined;
    linkTo?: string;
    decimalScale?: number;
    getSuffix?: (option: TOption | string | null) => React.ReactNode;
}

const LINK_CLASS = 'text-fg hover:text-link no-underline hover:underline';

// If value is string or number, then render it. Else return '-'.
const formatTextValue = <TOption extends FieldValues>(textValue: string | TOption | TOption[] | null) => {
    if (typeof textValue === 'string' || typeof textValue === 'number') {
        return textValue;
    } else {
        return '-';
    }
};

const ReadonlyField = <TOption extends FieldValues>({
    value,
    getValue,
    getLabel,
    linkTo,
    decimalScale,
    getSuffix,
}: Props<TOption>) => {
    // In a record section the text is a read value's, which is medium.
    const weight = useInRecordSection() ? 'font-medium' : 'font-normal';
    // If is value list, then render all items in list.
    if (Array.isArray(value)) {
        const links = value.map((option, index) => {
            const maybeId = getValue && getValue(option) ? getValue(option) : null;
            const textValue = getLabel && getLabel(option) ? getLabel(option) : option;
            const suffixValue = (getSuffix && getSuffix(option)) ?? undefined;
            const optionKey = maybeId != null ? `option-${maybeId}` : `option-${String(textValue)}`;

            // Assume that the value is an id. If id exists and linkTo is set then link to the person, office etc page. Else just return text value.
            return (
                <React.Fragment key={optionKey}>
                    {maybeId && textValue && linkTo ? (
                        <>
                            <RecordLink
                                to={`/${linkTo}/${maybeId}`}
                                className={LINK_CLASS}
                                label={String(formatTextValue(textValue))}
                            />
                            {suffixValue ? <span className="ml-[0.5ch]">{suffixValue}</span> : null}
                            {index < value.length - 1 ? <span>,&nbsp;</span> : null}
                        </>
                    ) : (
                        <p className={cn('text-fg flex w-fit text-sm', weight)}>
                            {formatTextValue(textValue)}
                            {index < value.length - 1 ? <span>,&nbsp;</span> : null}
                        </p>
                    )}
                </React.Fragment>
            );
        });

        return <div className="flex min-h-5 flex-wrap">{links?.length ? links : '-'}</div>;
    }

    const maybeId = typeof value !== 'string' && getValue && getValue(value) ? getValue(value) : null;
    const textValue = typeof value !== 'string' && getLabel && getLabel(value) ? getLabel(value) : value;
    const suffixValue = (getSuffix && getSuffix(value)) ?? undefined;

    // Assume that the value is an id. If id exists and linkTo is set then link to the person, office etc page. Else just return text value.
    if (maybeId && linkTo) {
        const label = textValue !== '' && textValue !== null ? formatTextValue(textValue) : '-';
        return (
            <div className="flex flex-row gap-[0.5ch]">
                <RecordLink to={`/${linkTo}/${maybeId}`} className={LINK_CLASS} label={String(label)} />
                {suffixValue ? <span>{suffixValue}</span> : null}
            </div>
        );
    }
    // Number formatting is opt-in through decimalScale. A readonly text field renders its value verbatim,
    // so digit-only text (case number, reg code, personal id) keeps the exact characters it was given.
    const numericValue =
        decimalScale !== undefined &&
        (typeof textValue === 'number' || (typeof textValue === 'string' && textValue.trim() !== ''))
            ? Number(textValue)
            : NaN;
    const isNumber = Number.isFinite(numericValue);
    return (
        <span className={cn('text-fg flex min-h-5 w-fit text-sm break-words whitespace-pre-wrap', weight)}>
            {isNumber ? (
                <NumberFormat displayType="text" fixedDecimalScale decimalScale={decimalScale} value={numericValue} />
            ) : textValue !== '' && textValue !== null ? (
                formatTextValue(textValue)
            ) : (
                '-'
            )}
            {suffixValue ? <span className="ml-[0.5ch]">{suffixValue}</span> : null}
        </span>
    );
};

export default ReadonlyField;
